import api from './api';

const UNLOCK_KEY = 'staffAiUnlock';

const unlockHeaders = () => {
  const t = sessionStorage.getItem(UNLOCK_KEY);
  return t ? { headers: { 'x-staff-ai-unlock': t } } : {};
};

export interface StaffAiStatus {
  on: boolean;
  configured: boolean;
  requireAuthenticator: boolean;
  voices: { value: string; label: string }[];
  languages?: string[];
  user: { id: number; name: string };
}

export interface StaffAiTaskBrief {
  id: string;
  status: string;
  priority: string | null;
  category: string | null;
  modality: string;
  createdAt: string | null;
  lastMessageAt: string | null;
  needsReply: boolean;
  lastPatientAt: string | null;
  lastPatientText: string;
  summary: string[];
  assignedTo: string | null;
  followUpDate: string | null;
  open: boolean;
  canText: boolean;
}

export interface StaffAiPatientOverview {
  patient: {
    lmdId: string;
    name: string;
    dob: string | null;
    phone: string | null;
    email: string | null;
    entityId: string;
    phoneLast4: string | null;
    archived: boolean;
    deceased: boolean;
  };
  openTasks: StaffAiTaskBrief[];
  recentTasks: StaffAiTaskBrief[];
  canText: boolean;
  noTextReason: string | null;
}

export interface StaffAiSendResult {
  taskId: string;
  newTask: boolean;
  sentMessage: StaffAiMessage | null;
  learning: { key: string } | null;
}

export interface StaffAiConversation {
  id: string;
  mode: 'general' | 'teach';
  title: string;
  createdAt: string;
  lastMessageAt: string | null;
}

export interface StaffAiMessage {
  id: string;
  role: 'user' | 'assistant' | 'teach' | 'sent' | 'action';
  content: string;
  meta?: any;
  createdAt: string;
}

export interface StaffAiPreferences {
  voice: string;
  length: 'short' | 'normal' | 'detailed';
  instructions: string;
}

export interface StaffAiPatientHit {
  lmdId: string;
  name: string;
  dob: string | null;
  phoneLast4: string | null;
  archived: boolean;
  identifiable: boolean;
}

export interface StaffAiPatient {
  lmdId: string;
  name: string;
  dob: string | null;
}

export interface StaffAiPatientThread {
  id: string;
  title: string;
  taskId?: string | null;
  createdBy?: string;
  createdAt: string;
  lastMessageAt: string | null;
}

const data = <T,>(p: Promise<any>): Promise<T> => p.then((r) => r.data.data as T);

export const staffAiApi = {
  status: () => data<StaffAiStatus>(api.get('/staff-ai/status')),
  unlock: async (code: string) => {
    const r = await data<{ token: string }>(api.post('/staff-ai/unlock', { code }));
    sessionStorage.setItem(UNLOCK_KEY, r.token);
  },
  clearUnlock: () => sessionStorage.removeItem(UNLOCK_KEY),

  getPreferences: () => data<StaffAiPreferences>(api.get('/staff-ai/preferences', unlockHeaders())),
  putPreferences: (p: StaffAiPreferences) => data<StaffAiPreferences>(api.put('/staff-ai/preferences', p, unlockHeaders())),

  listConversations: () => data<StaffAiConversation[]>(api.get('/staff-ai/conversations', unlockHeaders())),
  createConversation: (mode: 'general' | 'teach') => data<StaffAiConversation>(api.post('/staff-ai/conversations', { mode }, unlockHeaders())),
  getMessages: (id: string) => data<StaffAiMessage[]>(api.get(`/staff-ai/conversations/${id}/messages`, unlockHeaders())),
  sendMessage: (id: string, content: string, teach = false, teachKind = 'knowledge', approve = false, web = true, practice = false) =>
    data<{ assistantMessage: StaffAiMessage; teachDraft?: any }>(api.post(`/staff-ai/conversations/${id}/messages`, { content, teach, teachKind, approve, web, practice }, { ...unlockHeaders(), timeout: 200000 })),
  deleteConversation: (id: string) => data<boolean>(api.delete(`/staff-ai/conversations/${id}`, unlockHeaders())),

  searchPatients: (q: string) => data<StaffAiPatientHit[]>(api.get('/staff-ai/patients/search', { params: { q }, ...unlockHeaders() })),
  openPatient: (lmdId: string) =>
    data<{ patient: StaffAiPatient; threads: StaffAiPatientThread[]; overview: StaffAiPatientOverview | null }>(
      api.post(`/staff-ai/patients/${encodeURIComponent(lmdId)}/open`, {}, unlockHeaders())),
  patientOverview: (lmdId: string) =>
    data<StaffAiPatientOverview>(api.get(`/staff-ai/patients/${encodeURIComponent(lmdId)}/overview`, unlockHeaders())),
  sendToPatient: (lmdId: string, body: { text: string; taskId?: string; threadId?: string | null; aiDraft?: string; originalText?: string }) =>
    data<StaffAiSendResult>(api.post(`/staff-ai/patients/${encodeURIComponent(lmdId)}/send`, body, { ...unlockHeaders(), timeout: 150000 })),
  taskAction: (lmdId: string, taskId: string, body: { type: 'note' | 'done' | 'followup'; text?: string; date?: string; threadId?: string | null }) =>
    data<{ taskId: string; type: string; entry: StaffAiMessage | null }>(
      api.post(`/staff-ai/patients/${encodeURIComponent(lmdId)}/tasks/${encodeURIComponent(taskId)}/action`, body, { ...unlockHeaders(), timeout: 150000 })),
  taskThread: (lmdId: string, taskId: string) =>
    data<StaffAiPatientThread>(api.post(`/staff-ai/patients/${encodeURIComponent(lmdId)}/tasks/${encodeURIComponent(taskId)}/thread`, {}, unlockHeaders())),
  translate: (text: string, language: string) =>
    data<{ text: string; language: string }>(api.post('/staff-ai/translate', { text, language }, { ...unlockHeaders(), timeout: 200000 })),
  createPatientThread: (lmdId: string, title?: string) =>
    data<StaffAiPatientThread>(api.post(`/staff-ai/patients/${encodeURIComponent(lmdId)}/threads`, { title }, unlockHeaders())),
  getPatientMessages: (lmdId: string, threadId: string) =>
    data<StaffAiMessage[]>(api.get(`/staff-ai/patients/${encodeURIComponent(lmdId)}/threads/${threadId}/messages`, unlockHeaders())),
  sendPatientMessage: (lmdId: string, threadId: string, content: string, teach = false, teachKind = 'knowledge', approve = false) =>
    data<{ userMessage?: StaffAiMessage; assistantMessage: StaffAiMessage; teachDraft?: any }>(
      api.post(`/staff-ai/patients/${encodeURIComponent(lmdId)}/threads/${threadId}/messages`, { content, teach, teachKind, approve }, { ...unlockHeaders(), timeout: 200000 })),

  approveTeach: (key: string) => data<{ key: string; approved: boolean }>(api.post(`/staff-ai/teach/${encodeURIComponent(key)}/approve`, {}, { ...unlockHeaders(), timeout: 90000 })),
  teachStatus: (keys: string[]) =>
    data<StaffAiTeachStatus[]>(api.get('/staff-ai/teach/status', { params: { keys: keys.join(',') }, ...unlockHeaders() })),
  uploadTeach: (id: string, file: File, teachKind = 'knowledge', approve = false) =>
    data<{ assistantMessage: StaffAiMessage; teachDraft?: any; upload?: any }>(
      api.post(`/staff-ai/conversations/${id}/upload`, uploadForm(file, { kind: teachKind, approve }), uploadConfig())),
  uploadPatient: (lmdId: string, threadId: string, file: File, opts: { teach: boolean; teachKind: string; approve: boolean }) =>
    data<{ userMessage: StaffAiMessage; assistantMessage: StaffAiMessage; teachDraft?: any; upload?: any }>(
      api.post(`/staff-ai/patients/${encodeURIComponent(lmdId)}/threads/${threadId}/upload`,
        uploadForm(file, { teach: opts.teach, kind: opts.teachKind, approve: opts.approve }), uploadConfig())),
};

function uploadConfig() {
  const unlock = (unlockHeaders() as any).headers || {};
  return { headers: { 'Content-Type': 'multipart/form-data', ...unlock }, timeout: 600000 };
}

export interface StaffAiTeachStatus {
  key: string;
  version: number;
  status: 'draft' | 'approved' | 'rejected' | 'retired';
  reviewedBy?: string | null;
  reviewedAt?: string | null;
}

function uploadForm(file: File, fields: Record<string, string | boolean>): FormData {
  const f = new FormData();
  f.append('file', file);
  Object.entries(fields).forEach(([k, v]) => f.append(k, String(v)));
  return f;
}

export const staffAiError = (e: any): { code: string; message: string } => ({
  code: e?.response?.data?.error || 'failed',
  message: e?.response?.data?.message || e?.message || 'Something went wrong.',
});
